import React, { useState } from 'react';
import { faEnvelope, faExclamationCircle, faExclamationTriangle, faEye, faLock, faSpinner } from '@fortawesome/free-solid-svg-icons';
import { faFacebook, faGoogle } from '@fortawesome/free-brands-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useAuthentication } from '@features/auth/useAuthentication.ts';

interface ComponentProps {
  returnUrl?: string;
}

const Login: React.FC<ComponentProps> = ({ returnUrl }) => {
  const { login, error, clearError } = useAuthentication();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const handleOnSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email || !password) {
      return;
    }

    // Vymažeme předchozí chybu před novým pokusem
    clearError();
    setIsLoading(true);

    try {
      await login(email, password, returnUrl);
      // Po úspěšném přihlášení se stránka přesměruje, takže pole se nevymažou
    } catch (error) {
      console.error('Login failed:', error);
      // Při chybě pole zůstávají vyplněná, takže uživatel nemusí znovu zadávat údaje
    } finally {
      setIsLoading(false);
    }
  };

  const togglePassword = () => {
    setShowPassword(!showPassword);
  };

  return (
    <>
      <section id="auth-container" className="flex flex-row pt-24">
        <div className="max-w-6xl mx-auto">
          <div id="auth-forms" className="max-w-md mx-auto w-full">
            <div id="login-form" className="bg-white rounded-2xl shadow-xl p-8">
              <div className="text-center mb-8">
                <h2 className="text-3xl font-bold text-gray-900 mb-2">Přihlášení</h2>
                <p className="text-gray-600">Přihlaste se do svého účtu Reservare</p>
              </div>

              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6">
                  <div className="flex items-center">
                    <FontAwesomeIcon icon={faExclamationCircle} className="mr-2" />
                    <span>{error}</span>
                  </div>
                </div>
              )}

              <form id="loginForm" className="space-y-6" onSubmit={handleOnSubmit}>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">E-mailová adresa</label>
                  <div className="relative">
                    <input
                      type="email"
                      id="login-email"
                      className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-lg focus:border-primary-500 focus:ring-2 focus:ring-primary-200 focus:outline-none transition-colors"
                      placeholder="vas@email.cz"
                      value={email}
                      onChange={e => setEmail(e.target.value)}
                      required
                    />
                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                      <FontAwesomeIcon icon={faEnvelope} className="text-gray-400" />
                    </div>
                  </div>
                  <div id="email-error" className="hidden text-red-600 text-sm mt-1">
                    <FontAwesomeIcon icon={faExclamationTriangle} className="mr-1" />
                    Zadejte platnou e-mailovou adresu
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Heslo</label>
                  <div className="relative">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      id="login-password"
                      className="w-full pl-12 pr-12 py-3 border border-gray-300 rounded-lg focus:border-primary-500 focus:ring-2 focus:ring-primary-200 focus:outline-none transition-colors"
                      placeholder="Vaše heslo"
                      value={password}
                      onChange={e => setPassword(e.target.value)}
                      required
                    />
                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                      <FontAwesomeIcon icon={faLock} className="text-gray-400" />
                    </div>
                    <button type="button" className="absolute inset-y-0 right-0 pr-4 flex items-center" onClick={togglePassword}>
                      <FontAwesomeIcon icon={faEye} className="text-gray-400 hover:text-gray-600" />
                    </button>
                  </div>
                  <div id="password-error" className="hidden text-red-600 text-sm mt-1">
                    <FontAwesomeIcon icon={faExclamationTriangle} className="mr-1" />
                    Heslo je povinné
                  </div>
                </div>

                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <input type="checkbox" id="remember-me" className="h-4 w-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500" />
                    <label htmlFor="remember-me" className="ml-2 text-sm text-gray-600">
                      Zapamatovat si mě
                    </label>
                  </div>
                  <span className="text-sm text-primary-600 hover:text-primary-700 font-medium cursor-pointer">Zapomněli jste heslo?</span>
                </div>

                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full bg-primary-600 text-white py-3 rounded-lg text-lg font-semibold hover:bg-primary-700 transition-colors duration-200 flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <span>{isLoading ? 'Přihlašuji...' : 'Přihlásit se'}</span>
                  {isLoading && <FontAwesomeIcon icon={faSpinner} className="fa-spin ml-2" />}
                </button>
              </form>

              <div className="mt-8">
                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-gray-300"></div>
                  </div>
                  <div className="relative flex justify-center text-sm">
                    <span className="px-4 bg-white text-gray-500">Nebo se přihlaste pomocí</span>
                  </div>
                </div>

                <div className="mt-6 grid grid-cols-2 gap-3">
                  <button className="w-full inline-flex justify-center py-3 px-4 border border-gray-300 rounded-lg shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50">
                    <FontAwesomeIcon icon={faGoogle} className="text-red-500 mr-2" />
                    Google
                  </button>
                  <button className="w-full inline-flex justify-center py-3 px-4 border border-gray-300 rounded-lg shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50">
                    <FontAwesomeIcon icon={faFacebook} className="text-blue-600 mr-2" />
                    Facebook
                  </button>
                </div>
              </div>

              <div className="mt-8 text-center">
                <p className="text-gray-600">
                  Nemáte ještě účet?
                  <button className="text-primary-600 hover:text-primary-700 font-semibold">Registrujte se zdarma</button>
                </p>
              </div>
            </div>

            {/*<div id="register-form" className="hidden bg-white rounded-2xl shadow-xl p-8">
              <div className="text-center mb-8">
                <h2 className="text-3xl font-bold text-gray-900 mb-2">Registrace</h2>
                <p className="text-gray-600">Vytvořte si účet a začněte používat HairBook</p>
              </div>

              <div id="registration-steps" className="flex items-center justify-between mb-8">
                <div className="flex items-center">
                  <div className="w-8 h-8 bg-primary-600 text-white rounded-full flex items-center justify-center text-sm font-semibold">1</div>
                  <span className="ml-2 text-sm font-medium text-primary-600">Účet</span>
                </div>
                <div className="flex-1 h-px bg-gray-300 mx-4"></div>
                <div className="flex items-center">
                  <div className="w-8 h-8 bg-gray-200 text-gray-500 rounded-full flex items-center justify-center text-sm font-semibold">2</div>
                  <span className="ml-2 text-sm font-medium text-gray-500">Salon</span>
                </div>
                <div className="flex-1 h-px bg-gray-300 mx-4"></div>
                <div className="flex items-center">
                  <div className="w-8 h-8 bg-gray-200 text-gray-500 rounded-full flex items-center justify-center text-sm font-semibold">3</div>
                  <span className="ml-2 text-sm font-medium text-gray-500">Tarif</span>
                </div>
              </div>

              <div id="register-error" className="hidden bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6">
                <div className="flex items-start">
                  <FontAwesomeIcon icon={faExclamationCircle} className="mr-2 mt-0.5" />
                  <div>
                    <p className="font-semibold">Registrace se nezdařila</p>
                    <ul id="register-error-list" className="text-sm mt-1 space-y-1"></ul>
                  </div>
                </div>
              </div>

              <form id="registerForm" className="space-y-6">
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Jméno <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      id="register-firstname"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:border-primary-500 focus:ring-2 focus:ring-primary-200 focus:outline-none transition-colors"
                      placeholder="Vaše jméno"
                    />
                    <div id="firstname-error" className="hidden text-red-600 text-sm mt-1">
                      <FontAwesomeIcon icon={faExclamationTriangle} className="mr-1" />
                      Jméno je povinné
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Příjmení <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      id="register-lastname"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:border-primary-500 focus:ring-2 focus:ring-primary-200 focus:outline-none transition-colors"
                      placeholder="Vaše příjmení"
                    />
                    <div id="lastname-error" className="hidden text-red-600 text-sm mt-1">
                      <FontAwesomeIcon icon={faExclamationTriangle} className="mr-1" />
                      Příjmení je povinné
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    E-mailová adresa <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <input
                      type="email"
                      id="register-email"
                      className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-lg focus:border-primary-500 focus:ring-2 focus:ring-primary-200 focus:outline-none transition-colors"
                      placeholder="vas@email.cz"
                    />
                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                      <FontAwesomeIcon icon={faEnvelope} className="text-gray-400" />
                    </div>
                  </div>
                  <div id="register-email-error" className="hidden text-red-600 text-sm mt-1">
                    <FontAwesomeIcon icon={faExclamationTriangle} className="mr-1" />
                    <span>Zadejte platnou e-mailovou adresu</span>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Telefon <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <input
                      type="tel"
                      id="register-phone"
                      className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-lg focus:border-primary-500 focus:ring-2 focus:ring-primary-200 focus:outline-none transition-colors"
                      placeholder="+420 777 123 456"
                    />
                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                      <FontAwesomeIcon icon={faPhone} className="text-gray-400" />
                    </div>
                  </div>
                  <div id="phone-error" className="hidden text-red-600 text-sm mt-1">
                    <FontAwesomeIcon icon={faExclamationTriangle} className="mr-1" />
                    Telefon je povinný
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Heslo <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <input
                      type="password"
                      id="register-password"
                      className="w-full pl-12 pr-12 py-3 border border-gray-300 rounded-lg focus:border-primary-500 focus:ring-2 focus:ring-primary-200 focus:outline-none transition-colors"
                      placeholder="Minimálně 8 znaků"
                    />
                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                      <FontAwesomeIcon icon={faLock} className="text-gray-400" />
                    </div>
                    <button type="button" className="absolute inset-y-0 right-0 pr-4 flex items-center" onClick="togglePassword('register-password')">
                      <FontAwesomeIcon icon={faEye} className="text-gray-400 hover:text-gray-600" />
                    </button>
                  </div>
                  <div id="register-password-error" className="hidden text-red-600 text-sm mt-1">
                    <FontAwesomeIcon icon={faExclamationTriangle} className="mr-1" />
                    <span>Heslo musí mít minimálně 8 znaků</span>
                  </div>

                  <div id="password-strength" className="mt-2">
                    <div className="flex space-x-1">
                      <div className="h-1 flex-1 bg-gray-200 rounded"></div>
                      <div className="h-1 flex-1 bg-gray-200 rounded"></div>
                      <div className="h-1 flex-1 bg-gray-200 rounded"></div>
                      <div className="h-1 flex-1 bg-gray-200 rounded"></div>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      Síla hesla: <span id="password-strength-text">Slabé</span>
                    </p>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Potvrzení hesla <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <input
                      type="password"
                      id="register-password-confirm"
                      className="w-full pl-12 pr-12 py-3 border border-gray-300 rounded-lg focus:border-primary-500 focus:ring-2 focus:ring-primary-200 focus:outline-none transition-colors"
                      placeholder="Zopakujte heslo"
                    />
                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                      <FontAwesomeIcon icon={faLock} className="text-gray-400" />
                    </div>
                    <button type="button" className="absolute inset-y-0 right-0 pr-4 flex items-center" onClick="togglePassword('register-password-confirm')">
                      <FontAwesomeIcon icon={faEye} className="text-gray-400 hover:text-gray-600" />
                    </button>
                  </div>
                  <div id="password-confirm-error" className="hidden text-red-600 text-sm mt-1">
                    <FontAwesomeIcon icon={faExclamationTriangle} className="mr-1" />
                    Hesla se neshodují
                  </div>
                </div>

                <div className="space-y-4">
                  <div className="flex items-start space-x-3">
                    <input type="checkbox" id="terms-checkbox" className="mt-1 h-4 w-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500" />
                    <label htmlFor="terms-checkbox" className="text-sm text-gray-600">
                      Souhlasím s<span className="text-primary-600 hover:text-primary-700 font-medium cursor-pointer">obchodními podmínkami</span>a
                      <span className="text-primary-600 hover:text-primary-700 font-medium cursor-pointer">zásadami ochrany osobních údajů</span>
                      <span className="text-red-500">*</span>
                    </label>
                  </div>
                  <div id="terms-error" className="hidden text-red-600 text-sm">
                    <FontAwesomeIcon icon={faExclamationTriangle} className="mr-1" />
                    Musíte souhlasit s podmínkami
                  </div>

                  <div className="flex items-start space-x-3">
                    <input type="checkbox" id="newsletter-checkbox" className="mt-1 h-4 w-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500" />
                    <label htmlFor="newsletter-checkbox" className="text-sm text-gray-600">
                      Chci dostávat novinky a tipy pro kadeřnictví e-mailem
                    </label>
                  </div>
                </div>

                <button
                  type="submit"
                  className="w-full bg-primary-600 text-white py-3 rounded-lg text-lg font-semibold hover:bg-primary-700 transition-colors duration-200 flex items-center justify-center"
                >
                  <span id="register-btn-text">Vytvořit účet</span>
                  <FontAwesomeIcon id="register-spinner" icon={faSpinner} className="hidden fa-spin ml-2" />
                </button>
              </form>

              <div className="mt-8 text-center">
                <p className="text-gray-600">
                  Již máte účet?
                  <button onClick="showLoginForm()" className="text-primary-600 hover:text-primary-700 font-semibold">
                    Přihlaste se
                  </button>
                </p>
              </div>
            </div>*/}
          </div>
        </div>
      </section>
    </>
  );
};

export default Login;
